import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { usePerformanceLab, LabPlayer } from "@/contexts/PerformanceLabContext";
import { analytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";

const POSITION_SLOTS = [
  { slot: "GK", label: "GK" },
  { slot: "CB1", label: "CB" },
  { slot: "CB2", label: "CB" },
  { slot: "LB", label: "LB" },
  { slot: "RB", label: "RB" },
  { slot: "DM", label: "DM" },
  { slot: "CM1", label: "CM" },
  { slot: "CM2", label: "CM" },
  { slot: "LW", label: "LW" },
  { slot: "RW", label: "RW" },
  { slot: "ST", label: "ST" },
  { slot: "SUB1", label: "SUB" },
  { slot: "SUB2", label: "SUB" },
  { slot: "SUB3", label: "SUB" },
  { slot: "SUB4", label: "SUB" },
  { slot: "SUB5", label: "SUB" },
];

interface LineupBuilderProps {
  fixtureId: string;
  players: LabPlayer[];
}

function LineupBuilder({ fixtureId, players }: LineupBuilderProps) {
  const { translate } = useTranslation();
  const { session, setLineupEntry } = usePerformanceLab();
  const [activeSlot, setActiveSlot] = useState<string | null>(null);

  const fixtureLineup = session.lineups.filter((l) => l.fixture_id === fixtureId);
  const getPlayerForSlot = (slot: string) => {
    const entry = fixtureLineup.find((l) => l.position_slot === slot);
    if (!entry) return null;
    return players.find((p) => p.id === entry.player_id) ?? null;
  };
  const getSlotForPlayer = (playerId: string) =>
    fixtureLineup.find((l) => l.player_id === playerId)?.position_slot ?? null;

  const availablePlayers = players.filter(
    (p) => p.status === "fit" || p.is_hypothetical,
  );

  const handleSlotClick = (slot: string) => {
    setActiveSlot(activeSlot === slot ? null : slot);
  };

  const handlePlayerSelect = (slot: string, player: LabPlayer) => {
    // Remove player from any existing slot first
    const existingSlot = getSlotForPlayer(player.id);
    if (existingSlot && existingSlot !== slot) {
      setLineupEntry(fixtureId, existingSlot, null);
    }
    setLineupEntry(fixtureId, slot, player.id);
    analytics.track("lab.lineup_built", {
      fixture_id: fixtureId,
      is_hypothetical: player.is_hypothetical ?? false,
    });
    setActiveSlot(null);
  };

  const starters = POSITION_SLOTS.filter((s) => !s.slot.startsWith("SUB"));
  const subs = POSITION_SLOTS.filter((s) => s.slot.startsWith("SUB"));

  return (
    <div className="space-y-4">
      {/* Starters */}
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {translate("lab_lineup_starters")}
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {starters.map(({ slot, label }) => {
            const player = getPlayerForSlot(slot);
            const isActive = activeSlot === slot;
            return (
              <div key={slot} className="relative">
                <button
                  onClick={() => handleSlotClick(slot)}
                  className={cn(
                    "w-full rounded-md border px-2 py-1.5 text-xs text-left transition-colors",
                    player
                      ? "bg-primary/10 border-primary/30 text-foreground"
                      : "bg-muted/30 border-dashed text-muted-foreground hover:border-primary/50",
                    isActive && "ring-2 ring-primary ring-offset-1",
                  )}
                >
                  <span className="font-semibold text-[10px] text-muted-foreground block">
                    {label}
                  </span>
                  {player ? (
                    <span className="truncate flex items-center gap-1">
                      {player.is_hypothetical ? player.first_name : `${player.first_name[0]}. ${player.last_name}`}
                      {player.is_hypothetical && (
                        <span className="text-[9px] border rounded px-0.5 border-dashed">hypo</span>
                      )}
                    </span>
                  ) : (
                    <span>{translate("lab_lineup_empty_slot")}</span>
                  )}
                </button>

                {isActive && (
                  <div className="absolute z-20 left-0 top-full mt-1 bg-card border rounded-md shadow-xl p-1 w-44 max-h-48 overflow-y-auto">
                    {player && (
                      <button
                        onClick={() => { setLineupEntry(fixtureId, slot, null); setActiveSlot(null); }}
                        className="w-full text-left px-2 py-1.5 text-xs text-destructive hover:bg-muted rounded"
                      >
                        {translate("lab_lineup_remove")}
                      </button>
                    )}
                    {availablePlayers
                      .filter((p) => !getSlotForPlayer(p.id) || getSlotForPlayer(p.id) === slot)
                      .map((p) => (
                        <button
                          key={p.id}
                          onClick={() => handlePlayerSelect(slot, p)}
                          className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted rounded flex items-center gap-1"
                        >
                          {p.is_hypothetical ? p.first_name : `${p.first_name} ${p.last_name}`}
                          {p.is_hypothetical && (
                            <span className="text-[9px] border rounded px-0.5 border-dashed text-muted-foreground">hypo</span>
                          )}
                        </button>
                      ))}
                    {availablePlayers.filter((p) => !getSlotForPlayer(p.id) || getSlotForPlayer(p.id) === slot).length === 0 && (
                      <p className="text-xs text-muted-foreground px-2 py-1.5">{translate("lab_lineup_no_available")}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Subs */}
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {translate("lab_lineup_subs")}
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {subs.map(({ slot }) => {
            const player = getPlayerForSlot(slot);
            const isActive = activeSlot === slot;
            return (
              <div key={slot} className="relative">
                <button
                  onClick={() => handleSlotClick(slot)}
                  className={cn(
                    "w-full rounded-md border px-2 py-1.5 text-xs text-left transition-colors",
                    player
                      ? "bg-secondary/10 border-secondary/30 text-foreground"
                      : "bg-muted/30 border-dashed text-muted-foreground hover:border-primary/50",
                    isActive && "ring-2 ring-primary ring-offset-1",
                  )}
                >
                  <span className="font-semibold text-[10px] text-muted-foreground block">SUB</span>
                  {player ? (
                    <span className="truncate flex items-center gap-1">
                      {player.is_hypothetical ? player.first_name : `${player.first_name[0]}. ${player.last_name}`}
                      {player.is_hypothetical && (
                        <span className="text-[9px] border rounded px-0.5 border-dashed">hypo</span>
                      )}
                    </span>
                  ) : (
                    <span>{translate("lab_lineup_empty_slot")}</span>
                  )}
                </button>
                {isActive && (
                  <div className="absolute z-20 left-0 top-full mt-1 bg-card border rounded-md shadow-xl p-1 w-44 max-h-48 overflow-y-auto">
                    {player && (
                      <button
                        onClick={() => { setLineupEntry(fixtureId, slot, null); setActiveSlot(null); }}
                        className="w-full text-left px-2 py-1.5 text-xs text-destructive hover:bg-muted rounded"
                      >
                        {translate("lab_lineup_remove")}
                      </button>
                    )}
                    {availablePlayers
                      .filter((p) => !getSlotForPlayer(p.id) || getSlotForPlayer(p.id) === slot)
                      .map((p) => (
                        <button
                          key={p.id}
                          onClick={() => handlePlayerSelect(slot, p)}
                          className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted rounded"
                        >
                          {p.is_hypothetical ? p.first_name : `${p.first_name} ${p.last_name}`}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function LabFixturePanel() {
  const { translate } = useTranslation();
  const { session } = usePerformanceLab();
  const [selectedFixtureId, setSelectedFixtureId] = useState<string | null>(null);

  const selectedFixture = session.fixtures.find((f) => f.id === selectedFixtureId);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-4 py-3 border-b shrink-0">
        <h3 className="font-semibold text-sm">{translate("lab_fixture_panel_title")}</h3>
      </div>

      <div className="flex-1 overflow-hidden flex">
        {/* Fixture list */}
        <div className="w-52 shrink-0 border-r overflow-y-auto">
          {session.fixtures.length === 0 ? (
            <p className="text-xs text-muted-foreground p-4">{translate("lab_fixture_none")}</p>
          ) : (
            session.fixtures.map((f) => {
              const lineupCount = session.lineups.filter((l) => l.fixture_id === f.id).length;
              const isSelected = selectedFixtureId === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setSelectedFixtureId(f.id)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 border-b hover:bg-muted/50 transition-colors flex items-center gap-2",
                    isSelected && "bg-primary/10 border-l-2 border-l-primary",
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{f.opponent}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {format(parseISO(f.match_date), "d MMM")} · {f.venue}
                    </p>
                    {lineupCount > 0 && (
                      <Badge variant="secondary" className="text-[9px] h-4 mt-0.5">
                        {lineupCount} {translate("lab_lineup_assigned")}
                      </Badge>
                    )}
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
              );
            })
          )}
        </div>

        {/* Lineup builder */}
        <div className="flex-1 overflow-y-auto p-4">
          {!selectedFixture ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-muted-foreground">{translate("lab_fixture_select_prompt")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-sm">
                  {translate("lab_lineup_for")} {selectedFixture.opponent}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {format(parseISO(selectedFixture.match_date), "EEEE d MMMM yyyy")}
                  {selectedFixture.competition && ` · ${selectedFixture.competition}`}
                </p>
              </div>
              <LineupBuilder
                fixtureId={selectedFixture.id}
                players={session.players}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
